import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';
import {
  Activity, AlertTriangle, Database, Clock, Timer, Target,
  TrendingUp, TrendingDown, Zap,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';

const OverviewTab: React.FC<{ onNavigate: (k: any) => void; projectId?: number }> = ({ onNavigate, projectId }) => {
  const metrics = useQuery({
    queryKey: ['obs-metrics', projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.dashboardMetrics(projectId)).data.data,
    refetchInterval: 60000,
  });

  const anomalies = useQuery({
    queryKey: ['obs-recent-anomalies', projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.listAnomalies({
      limit: 5, ...(projectId ? { project_id: projectId } : {}),
    })).data.data,
    refetchInterval: 60000,
  });

  const tables = useQuery({
    queryKey: ['obs-tables-summary', projectId ?? 'all'],
    queryFn: async () => (await observabilityApi.listTables(projectId ? { project_id: projectId } : {})).data.data,
    refetchInterval: 60000,
  });

  const m = metrics.data || {};
  const open = m.open_incidents || {};
  const rv = m.resolution_velocity || {};
  const pillar = m.pillar_health || {};
  const delta = m.downtime_delta_pct;
  const deltaBetter = delta != null && delta < 0;

  return (
    <div className="space-y-6">
      {/* ── Row 1: 4 headline KPIs (no fabricated $$$) ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          icon={<Database className="w-4 h-4 text-indigo-500" />}
          label="Tables Monitored"
          value={m.total_tables ?? '—'}
          sub={`${m.anomalies_24h ?? 0} anomalies in last 24h`}
        />
        <Kpi
          icon={<AlertTriangle className={`w-4 h-4 ${open.total ? 'text-red-500' : 'text-emerald-500'}`} />}
          label="Open Incidents"
          value={open.total ?? 0}
          sub={`${open.high || 0} high · ${open.medium || 0} med · ${open.low || 0} low`}
          valueColor={open.total ? 'text-red-600' : 'text-emerald-600'}
        />
        <Kpi
          icon={<Clock className="w-4 h-4 text-amber-500" />}
          label="Data Downtime (7d)"
          value={`${m.downtime_7d_hours ?? 0}h`}
          sub={delta == null ? 'Baseline period' : (
            <span className={deltaBetter ? 'text-emerald-600' : 'text-red-600'}>
              {deltaBetter ? <TrendingDown className="inline w-3 h-3" /> : <TrendingUp className="inline w-3 h-3" />}
              {' '}{Math.abs(delta).toFixed(1)}% vs prev 7d
            </span>
          )}
        />
        <Kpi
          icon={<Zap className="w-4 h-4 text-emerald-500" />}
          label="Resolution Velocity"
          value={rv.ratio != null ? `${(rv.ratio * 100).toFixed(0)}%` : '—'}
          sub={`${rv.opened || 0} opened → ${rv.resolved || 0} resolved`}
          valueColor={(rv.ratio || 0) >= 0.8 ? 'text-emerald-600' : 'text-amber-500'}
        />
      </div>

      {/* ── Row 2: PILLAR HEALTH — our unique view ── */}
      <div className="bg-white rounded-xl border border-cargo-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="w-4 h-4 text-indigo-500" />
            Pillar Health (last 7 days)
          </h3>
          <span className="text-xs text-cargo-muted">% of monitored time each pillar was healthy</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['freshness', 'volume', 'schema', 'quality'].map((p) => {
            const info = pillar[p];
            const pct = typeof info === 'number' ? info : (info?.pct ?? 100);
            const status = typeof info === 'object' ? info?.status : null;
            const openCount = typeof info === 'object' ? info?.open_incidents : 0;
            return <PillarBar key={p} name={p} value={pct} status={status} openCount={openCount} />;
          })}
        </div>
      </div>

      {/* ── Row 3: Our differentiators — Blast Radius + Top Problem Tables + TTR ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-red-500" />
            <h4 className="font-semibold text-sm">Blast Radius Impact</h4>
          </div>
          <div className="text-3xl font-bold text-cargo-text">
            {m.max_blast_radius ?? 0}
            <span className="text-sm text-cargo-muted ml-1">
              {(m.max_blast_radius ?? 0) === 1 ? 'table at risk' : 'tables at risk'}
            </span>
          </div>
          <div className="text-xs text-cargo-muted mt-1">
            worst case across {m.unhealthy_tables_count || 0} unhealthy {m.unhealthy_tables_count === 1 ? 'table' : 'tables'}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="w-4 h-4 text-amber-500" />
            <h4 className="font-semibold text-sm">Avg Time to Resolution</h4>
          </div>
          <div className="text-3xl font-bold text-cargo-text">
            {m.avg_ttr_hours != null ? formatHours(m.avg_ttr_hours) : '—'}
          </div>
          <div className="text-xs text-cargo-muted mt-1">
            across {m.incidents_7d ?? 0} incidents this week (prev: {m.incidents_prev_7d ?? 0})
          </div>
        </div>

        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <h4 className="font-semibold text-sm">Top Problem Tables (7d)</h4>
          </div>
          {(m.top_problem_tables || []).length === 0 ? (
            <div className="text-xs text-cargo-muted py-2">No incidents this week 🟢</div>
          ) : (
            <ul className="space-y-1 mt-1">
              {m.top_problem_tables.slice(0, 5).map((t: any) => (
                <li key={t.table} className="flex items-center justify-between text-sm">
                  <span className="truncate">{t.table}</span>
                  <span className="flex items-center gap-1.5">
                    <SevBadge sev={t.worst_severity} />
                    <span className="text-xs font-semibold tabular-nums">{t.incidents}×</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Row 4: Downtime trend ── */}
      <div className="bg-white rounded-xl border border-cargo-border p-5">
        <h3 className="font-semibold mb-3">⏱️ Data Downtime — Last 7 Days</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={m.daily_downtime || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" fontSize={10}
                tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} />
              <YAxis fontSize={10} tickFormatter={(v) => `${v}h`} />
              <Tooltip
                formatter={(v: any, name: any) =>
                  name === 'downtime_hours' ? [`${v}h`, 'Downtime'] : [v, 'Incidents']}
                labelFormatter={(d) => new Date(d).toLocaleDateString()}
              />
              <Bar dataKey="downtime_hours" radius={[4, 4, 0, 0]}>
                {(m.daily_downtime || []).map((d: any, i: number) => (
                  <Cell key={i}
                    fill={d.downtime_hours === 0 ? '#10b981' : d.downtime_hours < 2 ? '#f59e0b' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Row 5: Recent anomalies + tables at glance ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Recent Anomalies</h3>
            <button onClick={() => onNavigate('anomalies')} className="text-xs text-indigo-600">View all →</button>
          </div>
          {(anomalies.data || []).length === 0 ? (
            <div className="text-sm text-cargo-muted py-8 text-center">🟢 No anomalies detected recently.</div>
          ) : (
            <ul className="space-y-2">
              {(anomalies.data || []).map((a: any) => (
                <li key={a.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50">
                  <SevBadge sev={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{a.table_name}</div>
                    <div className="text-xs text-cargo-muted line-clamp-2">{a.message}</div>
                    <div className="text-[11px] text-cargo-muted mt-0.5">{timeAgo(a.detected_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-cargo-border p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Tables at a Glance</h3>
            <button onClick={() => onNavigate('tables')} className="text-xs text-indigo-600">View all →</button>
          </div>
          {(tables.data || []).length === 0 ? (
            <div className="text-sm text-cargo-muted py-8 text-center">No tables registered for monitoring yet.</div>
          ) : (
            <ul className="space-y-2">
              {(tables.data || []).slice(0, 6).map((t: any) => (
                <li key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50">
                  <StatusDot status={t.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.table_name}</div>
                    <div className="text-xs text-cargo-muted">
                      {t.latest_row_count != null ? `${Number(t.latest_row_count).toLocaleString()} rows` : '—'}
                      {t.latest_staleness_seconds != null ? ` • ${formatStale(t.latest_staleness_seconds)} stale` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const Kpi: React.FC<{ icon?: React.ReactNode; label: string; value: any; sub?: any; valueColor?: string }> = ({ icon, label, value, sub, valueColor }) => (
  <div className="bg-white rounded-xl border border-cargo-border p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs text-cargo-muted">{label}</div>
      {icon}
    </div>
    <div className={`text-3xl font-bold mt-1 ${valueColor || 'text-cargo-text'}`}>{value}</div>
    <div className="text-[11px] text-cargo-muted mt-1">{sub}</div>
  </div>
);

const PillarBar: React.FC<{ name: string; value: number; status?: string | null; openCount?: number }> = ({ name, value, status, openCount }) => {
  const pct = Math.max(0, Math.min(100, value));
  // Status drives color: active incidents override the % threshold
  const color = status === 'critical' ? '#ef4444'
              : status === 'warning' ? '#f59e0b'
              : status === 'healthy' ? '#10b981'
              // Fallback when status not provided: old threshold logic
              : pct >= 98 ? '#10b981' : pct >= 90 ? '#f59e0b' : '#ef4444';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="capitalize font-medium flex items-center gap-1.5">
          {name}
          {(openCount ?? 0) > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
              {openCount} open
            </span>
          )}
        </span>
        <span className="font-semibold tabular-nums" style={{ color }}>{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

const SevBadge: React.FC<{ sev: string }> = ({ sev }) => {
  const color = sev === 'high' ? 'bg-red-100 text-red-700' : sev === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${color}`}>{sev}</span>;
};

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'anomaly' ? 'bg-red-500' : status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />;
};

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatStale(sec: number): string {
  if (sec < 60) return `${Math.floor(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

function formatHours(hr: number): string {
  if (hr < 1) return `${Math.round(hr * 60)}m`;
  return `${hr.toFixed(1)}h`;
}

export default OverviewTab;
